import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { AccountSuspendedModal } from '../components/modals/AccountSuspendedModal';
import { useQueryClient } from '@tanstack/react-query';
import { LocalStorageKeys } from '../constants/locationStorage';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageRoutes } from '../constants/routes';
import { getProfile } from '../services/ProfileService';
import { ProfileResponseModel } from '../types/Profile';
import { useMediaQuery, useViewportSize } from '@mantine/hooks';
import { useAuth } from '../auth/AuthContext';
import { normalizeProfileFollowData } from '../services/followingProfile';

interface AppContextType {
  token: string;
  onSetProfile: (profile: ProfileResponseModel | null) => void;
  profile?: ProfileResponseModel | null;
  getProfileAndRedirect: (
    token: string,
    redirectPath?: string | null
  ) => Promise<void>;
  logout: () => void;
  topPadding: number;
  scrollAreaHeight: string;
  isTabletAndBelow: boolean;
  stripeSetupIssues: boolean;
  setStripeSetupIssues: (hasIssues: boolean) => void;
  accountSuspended: boolean;
}

interface AppContextProps {
  children: ReactNode;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<AppContextProps> = ({ children }) => {
  const { accessToken, logout: authLogout } = useAuth();
  const storedProfileRaw = localStorage.getItem(LocalStorageKeys.PROFILE);
  const [profile, setProfile] = useState<ProfileResponseModel | null>(
    storedProfileRaw
      ? normalizeProfileFollowData(JSON.parse(storedProfileRaw) as ProfileResponseModel)
      : null
  );
  const [stripeSetupIssues, setStripeSetupIssues] = useState<boolean>(false);
  const [accountSuspended, setAccountSuspended] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const isTabletAndBelow = useMediaQuery('(max-width: 64em)');
  const { height: viewportHeight } = useViewportSize();

  const getTopPadding = useMemo(() => {
    return 0;
  }, []);

  const scrollAreaHeight = useMemo(() => {
    if (isTabletAndBelow) {
      // 50px mobile top bar + 60px bottom nav = 110px overhead
      return `${viewportHeight - 110}px`;
    }
    // 120px desktop header
    return `${viewportHeight - 120}px`;
  }, [isTabletAndBelow, viewportHeight]);

  // check user stripe status
  useEffect(() => {
    // Skip on login/signup pages
    if (
      location.pathname === PageRoutes.LOGIN ||
      location.pathname.includes('signup')
    ) {
      return;
    }

    // TODO implement stripe status check

    const fetchProfile = async () => {
      try {
        const data = await queryClient.fetchQuery<ProfileResponseModel>({
          enabled: !!accessToken,
          queryKey: ['loadProfile', accessToken],
          queryFn: () => getProfile(accessToken as string),
          retry: 3,
        });
        if (
          data?.paymentAccountRequirementsCurrentlyDue ||
          data?.paymentAccountRequirementsPastDue
        ) {
          console.log('stripe status needs actions');
          setStripeSetupIssues(true);
        } else {
          console.log('stripe status does not need actions');

          setStripeSetupIssues(false);
        }
      } catch (err) {
        console.error('Error fetching profile for stripe check:', err);
      }
    };

    if (profile?.isProfessional) {
      fetchProfile();
    } else {
      console.log('Skipping stripe status for none professional');
    }
  }, [profile]);

  const getProfileAndRedirect = async (
    token: string,
    redirectPath?: string | null
  ) => {
    const storedRedirect = localStorage.getItem(
      LocalStorageKeys.POST_LOGIN_REDIRECT
    );
    const destination = redirectPath ?? storedRedirect ?? null;
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['loadProfile', token],
        queryFn: () => getProfile(token),
        retry: 3,
      });
      if (data) {
        if (data.enabled === false) {
          onSetProfile(data);
          setAccountSuspended(true);
          return;
        }
        onSetProfile(data);
        if (data?.id) {
          if (destination) {
            navigate(destination);
          } else {
            navigate(PageRoutes.SEARCH);
          }
          localStorage.removeItem(LocalStorageKeys.POST_LOGIN_REDIRECT);
        }
      }
    } catch (err) {
      console.error('Error fetching profile:', err); // TODO work out to handle third error
    }
  };

  const onSetProfile = (newProfile: ProfileResponseModel | null) => {
    const normalizedProfile = newProfile
      ? normalizeProfileFollowData(newProfile)
      : null;

    setProfile(normalizedProfile);
    if (normalizedProfile) {
      localStorage.setItem(
        LocalStorageKeys.PROFILE,
        JSON.stringify(normalizedProfile)
      );
    } else {
      localStorage.removeItem(LocalStorageKeys.PROFILE);
    }
  };

  const logout = async () => {
    onSetProfile(null);
    setAccountSuspended(false);
    try {
      await authLogout();
    } catch (err) {
      console.error('Error logging out:', err);
    } finally {
      queryClient.clear();
      localStorage.removeItem(LocalStorageKeys.IS_AUTHENTICATED);
      localStorage.removeItem(LocalStorageKeys.POST_LOGIN_REDIRECT);
      navigate(PageRoutes.LOGIN, { replace: true });
    }
  };

  return (
    <AppContext.Provider
      value={{
        token: accessToken || '',
        onSetProfile,
        profile,
        getProfileAndRedirect,
        logout,
        topPadding: getTopPadding,
        scrollAreaHeight,
        isTabletAndBelow,
        stripeSetupIssues,
        setStripeSetupIssues,
        accountSuspended,
      }}
    >
      <AccountSuspendedModal />
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);

  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }

  return context;
};
